/**
 * <stock-alert>: « Me prévenir du retour » button of the product page (sections/main-product.liquid) while the
 * selected format is sold out. A signed-in customer is subscribed at once with their account e-mail
 * (`data-email`); anyone else gets a modal <dialog> with the e-mail field focused. The e-mail is subscribed to
 * Klaviyo's back-in-stock alert for that variant (client API, public key only); Klaviyo's « Back in Stock » flow
 * sends the e-mail when the stock comes back.
 * Follows the selected format (`product:variant`, assets/product-page.js). Sold-out formats are disabled radios in
 * the HTML (nothing to do with them without JS); with this button they become selectable. Without JS the button
 * stays hidden (html.no-js).
 */

export const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
export const REVISION = '2025-10-15';

/**
 * Klaviyo's request body for a back-in-stock subscription by e-mail to a Shopify variant (default catalog).
 *
 * @param {string} email
 * @param {string | number} variantId
 */
export function subscriptionBody(email, variantId) {
  return {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: {
        variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
      },
    },
  };
}

/**
 * Subscribes `email` to the return of `variantId`. Rejects when Klaviyo refuses it (invalid e-mail, unknown
 * variant…) or cannot be reached.
 *
 * @param {string} companyId - Klaviyo public key (site ID).
 * @param {string} email
 * @param {string | number} variantId
 */
export async function subscribe(companyId, email, variantId) {
  const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
    method: 'POST',
    headers: { revision: REVISION, 'Content-Type': 'application/vnd.api+json' },
    body: JSON.stringify(subscriptionBody(email, variantId)),
  });
  if (!response.ok) throw new Error(`Klaviyo back in stock: ${response.status}`);
}

export class StockAlert extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('product:variant', this.onVariant);
    // The formats come before the button in the page: already parsed.
    const page = this.closest('product-page');
    for (const input of page?.querySelectorAll('input[name="id"][disabled]') ?? []) {
      if (input instanceof HTMLInputElement) input.disabled = false;
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('product:variant', this.onVariant);
  }

  get button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-alert-open]'));
  }

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }

  /** @param {DocumentEventMap['product:variant']} event */
  onVariant = (event) => {
    const { variant } = event.detail;
    this.hidden = variant.available;
    this.dataset.variant = String(variant.id);
    const label = this.querySelector('[data-alert-variant]');
    if (label) label.textContent = variant.title;
    this.reset();
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const dialog = this.dialog;
    // A click on the backdrop lands on the dialog itself.
    if (dialog && event.target === dialog) {
      dialog.close();
      return;
    }
    const button = this.button;
    if (!button || !(event.target instanceof Node) || !button.contains(event.target) || button.disabled) return;
    if (this.dataset.email) {
      this.send(this.dataset.email, this.dataset.okAccount);
    } else if (dialog && !dialog.open) {
      dialog.classList.remove('is-done');
      this.say('[data-alert-dlg-msg]', '', '');
      dialog.showModal();
    }
  };

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-alert-form]')) return;
    event.preventDefault();
    const input = form.querySelector('input[type="email"]');
    if (input instanceof HTMLInputElement) this.send(input.value.trim(), this.dataset.ok);
  };

  /**
   * Subscribes `email`, then says so under the button (and in the dialog when it is open).
   *
   * @param {string} email
   * @param {string | undefined} ok - Message once subscribed.
   */
  async send(email, ok) {
    const dialog = this.dialog?.open ? this.dialog : null;
    const busy = dialog ? dialog.querySelector('[data-alert-form] button') : this.button;
    if (!busy || busy.getAttribute('aria-busy') === 'true') return;
    busy.setAttribute('aria-busy', 'true');
    try {
      await subscribe(this.dataset.company ?? '', email, this.dataset.variant ?? '');
      this.say('[data-alert-msg]', ok ?? '', 'is-ok');
      if (dialog) {
        dialog.classList.add('is-done');
        this.say('[data-alert-dlg-msg]', ok ?? '', 'is-ok');
      }
      const button = this.button;
      if (button) {
        button.disabled = true;
        const label = button.querySelector('[data-alert-label]');
        if (label) label.textContent = this.dataset.done ?? '';
      }
    } catch {
      this.say(dialog ? '[data-alert-dlg-msg]' : '[data-alert-msg]', this.dataset.error ?? '', 'is-err');
    } finally {
      busy.removeAttribute('aria-busy');
    }
  }

  /** Back to the untouched button, for another format. */
  reset() {
    const button = this.button;
    if (button) {
      button.disabled = false;
      const label = button.querySelector('[data-alert-label]');
      if (label) label.textContent = this.dataset.open ?? label.textContent;
    }
    this.say('[data-alert-msg]', '', '');
    this.say('[data-alert-dlg-msg]', '', '');
    this.dialog?.classList.remove('is-done');
  }

  /**
   * @param {string} selector
   * @param {string} text
   * @param {'' | 'is-ok' | 'is-err'} state
   */
  say(selector, text, state) {
    const message = this.querySelector(selector);
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('is-ok', state === 'is-ok');
    message.classList.toggle('is-err', state === 'is-err');
  }
}

if (!customElements.get('stock-alert')) customElements.define('stock-alert', StockAlert);
